import { useEffect, useMemo, useState } from 'react';

import {
  AssetInspectionColumnId,
  BULK_PDF_EXPORT_LIMIT,
  GetInspectionsRequestDTO,
  InspectionDTO,
  InspectionOrderByFieldEnumSchema,
  MODULE,
} from '@gearbelt/global';
import { Button } from '@gearbelt/ui/components';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { RowSelectionState } from '@tanstack/react-table';
import { useDebounce } from '@uidotdev/usehooks';
import { useParams } from 'react-router-dom';
import { z } from 'zod';

import { DeleteConfirmationDialog } from '@/components/common/delete-confirmation-dialog';
import DeleteIcon from '@/components/icons/delete';
import DownloadCsvIcon from '@/components/icons/download-csv';
import DownloadPdfIcon from '@/components/icons/download-pdf';
import UndoIcon from '@/components/icons/undo';
import ColumnsDialog from '@/components/table-page/columns-dialog';
import { TablePageHeader, TablePageHeaderActionButton, TablePageTableGroupDropdown } from '@/components/table-page/table-page-header';
import TablePageSearchBar from '@/components/table-page/table-page-search-bar';
import { TablePageSelectedCountIndicator } from '@/components/table-page/table-page-selected-count-indicator';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';
import { useToast } from '@/components/ui/toaster';
import { useInfiniteScrollTrigger } from '@/hooks/use-infinite-scroll-trigger';
import { usePageConfig } from '@/hooks/use-page-config';
import { usePermissions } from '@/hooks/use-permissions';
import { useResetRowSelectionOnChange } from '@/hooks/use-reset-row-selection-on-change';
import { buildFilterFields } from '@/queries/column-filter';
import { inspectionKeys } from '@/queries/inspections';
import { getUnitsQueryOptions } from '@/queries/units';
import { ApiService } from '@/services/api-v2';
import { useModulesStore } from '@/stores/modules.store';
import { openExportTab } from '@/utils/export-tab';
import { buildCsvExportFilename } from '@/utils/export-to-csv';
import { buildPdfExportJob } from '@/utils/register-pdf-export';

import { AssetInspectionsTable, NoInspectionAvailableDisplay, NoInspectionFoundFromFiltersDisplay, NoInspectionFoundFromSearchDisplay } from './table';
import { FILTER_FIELD_MAP, groupOptions, columns as inspectionsColumns } from './table-meta';

const AssetInspectionsPage = () => {
  const { id } = useParams();

  const queryClient = useQueryClient();
  const toast = useToast();
  const { canEditInspectionRecord, canExportCsv } = usePermissions();

  const { setModuleFilters } = useModulesStore();

  const {
    config: { columnOrder, grouping, sorting, filters, columnVisibility },
    loading: isLoadingPageConfig,
    updatePageConfig,
  } = usePageConfig(MODULE.AssetInspections, {
    filterFieldMap: FILTER_FIELD_MAP,
    groupOptions,
    columns: inspectionsColumns(),
  });

  const [openColumnsDialog, setOpenColumnsDialog] = useState(false);
  const [openDeleteConfirmation, setOpenDeleteConfirmation] = useState(false);
  const [selectedRows, setSelectedRows] = useState<RowSelectionState>({});
  const [search, setSearch] = useState('');

  const debouncedSearch = useDebounce(search, 500);

  const inspectionFilters: GetInspectionsRequestDTO['filters'] = useMemo(
    () => ({
      search: debouncedSearch,
      ...buildFilterFields(filters, FILTER_FIELD_MAP, {
        transformValue: (fieldId, raw) => (fieldId === 'formNumber' ? (raw as string | undefined)?.replace(/[^0-9]/g, '') : raw),
        extraFields: { assetId: id ? [id] : undefined },
      }),
    }),
    [debouncedSearch, id, filters],
  );

  const inspectionGroupBy: GetInspectionsRequestDTO['groupBy'] = useMemo(
    () =>
      grouping.length
        ? { field: grouping[0] as NonNullable<GetInspectionsRequestDTO['groupBy']>['field'], direction: (sorting.direction || 'asc') as 'asc' | 'desc' }
        : undefined,
    [grouping, sorting.direction],
  );

  const inspectionOrderBy: GetInspectionsRequestDTO['orderBy'] = useMemo(
    () =>
      sorting.key
        ? {
            field: (sorting.key === 'assetInternalId' ? 'asset' : sorting.key) as z.infer<typeof InspectionOrderByFieldEnumSchema>,
            direction: sorting.direction as 'asc' | 'desc',
          }
        : undefined,
    [sorting.key, sorting.direction],
  );

  const {
    data: inspectionsData,
    fetchNextPage: fetchNextInspectionsPage,
    hasNextPage: hasNextInspectionsPage,
    isFetching: isFetchingInspections,
    isFetchingNextPage: isFetchingNextInspectionsPage,
    error: inspectionsError,
  } = useInfiniteQuery({
    queryKey: inspectionKeys.byAssetInfinite(id, inspectionFilters, inspectionGroupBy, inspectionOrderBy),
    queryFn: async ({ pageParam }: { pageParam?: string }) => {
      if (!id) throw new Error('No asset ID');

      return await (
        await ApiService.GetInspections({
          limit: 30,
          cursors: pageParam,
          filters: inspectionFilters,
          groupBy: inspectionGroupBy,
          orderBy: inspectionOrderBy,
        })
      ).json();
    },
    getNextPageParam: (lastPage) => (lastPage.pageInfo.hasNextPage ? lastPage.pageInfo.endCursor : undefined),
    initialPageParam: undefined,
    retry: false,
    gcTime: 0,
    enabled: !isLoadingPageConfig,
  });

  const [scrollRoot, setScrollRoot] = useState<HTMLDivElement | null>(null);
  const { sentinelRef, canLoadMore, loadMore } = useInfiniteScrollTrigger({
    fetchNextPage: fetchNextInspectionsPage,
    hasNextPage: hasNextInspectionsPage,
    isFetching: isFetchingInspections,
    isError: !!inspectionsError,
    root: scrollRoot,
  });

  const { data: units, isLoading: isLoadingUnits } = useQuery(getUnitsQueryOptions());

  const getSelectedInspections = useMemo(() => {
    return (incomingData: typeof inspectionsData) => {
      if (!incomingData?.pages) return [];
      const ids = Object.keys(selectedRows);
      const assets = incomingData.pages.flatMap((page) => page.data);
      return ids.filter((id) => !isNaN(parseInt(id))).map((id) => assets[parseInt(id)]);
    };
  }, [selectedRows]);

  const { mutate: deleteInspections, isPending: isDeletingInspections } = useMutation({
    mutationFn: async (ids: string[]) => await ApiService.DeleteInspections({ inspectionIds: ids }),
    onSuccess: () => {
      toast.success('Inspections deleted successfully');
      setOpenDeleteConfirmation(false);
      setSelectedRows({});

      queryClient.invalidateQueries({ queryKey: inspectionKeys.all });
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  const handleDeleteInspections = () => {
    if (inspectionsData?.pages) {
      const toDelete = getSelectedInspections(inspectionsData);
      deleteInspections(toDelete.map((inspection) => inspection.id));
    }
  };

  const selectedInspections = useMemo(() => getSelectedInspections(inspectionsData), [inspectionsData, getSelectedInspections]);
  const canBulkActOnSelectedInspections =
    selectedInspections.length > 0 && selectedInspections.every((inspection) => canEditInspectionRecord(inspection.inspectedById));

  function handleCsvExport() {
    if (!inspectionsData?.pages) return;

    const selected = getSelectedInspections(inspectionsData);

    const activeColumnIds = columnOrder.filter((column) => column.id !== 'select' && column.active).map((column) => column.id);
    openExportTab({
      kind: 'csv',
      request: {
        queryType: 'inspections',
        filename: buildCsvExportFilename('inspections'),
        filters: { fields: { id: selected.map((inspection) => inspection.id) } },
        groupBy: inspectionGroupBy,
        orderBy: inspectionOrderBy,
        columns: activeColumnIds,
      },
    });
  }

  function handlePdfExport() {
    if (!inspectionsData?.pages) return;
    const selected = getSelectedInspections(inspectionsData);
    if (selected.length > BULK_PDF_EXPORT_LIMIT) {
      toast.error(`You can export up to ${BULK_PDF_EXPORT_LIMIT} records at a time. Deselect some and try again.`);
      return;
    }
    openExportTab(buildPdfExportJob({ scope: 'records', recordType: 'inspection', records: selected }));
  }

  useEffect(() => {
    if (inspectionsError) {
      toast.error(inspectionsError.message);
    }
  }, [toast, inspectionsError]);

  useResetRowSelectionOnChange(setSelectedRows, [debouncedSearch, filters, sorting.key, sorting.direction, grouping]);

  const flattenedData = useMemo(() => inspectionsData?.pages.flatMap((page) => page.data) ?? [], [inspectionsData]);

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      <TablePageHeader>
        <div className="no-scrollbar h-full w-full overflow-x-auto py-0.5">
          <div className="flex h-full min-w-[580px] items-center justify-between">
            {!selectedInspections.length && (
              <>
                <div className="flex h-full items-center gap-x-2 px-2">
                  <TablePageSearchBar search={search} setSearch={setSearch} />
                  {filters && filters.length > 0 && (
                    <TablePageHeaderActionButton
                      icon={<UndoIcon />}
                      label="Remove all Filters"
                      kind="secondary"
                      onClick={() => setModuleFilters(MODULE.AssetInspections, [])}
                    />
                  )}
                </div>

                <div className="mr-1 flex h-full items-center">
                  <div className="flex items-center">
                    <TablePageTableGroupDropdown
                      columns={groupOptions}
                      value={grouping.length ? grouping[0] : ''}
                      onChange={(value) =>
                        updatePageConfig({
                          table: { grouping: value ? [value] : [] },
                        })
                      }
                    />
                    <ColumnsDialog<InspectionDTO>
                      open={openColumnsDialog}
                      defaultSectionLabel="Inspection"
                      onOpenChange={setOpenColumnsDialog}
                      columns={inspectionsColumns(units ?? [])}
                      columnOrder={columnOrder}
                      onColumnOrderChange={(columnOrder) =>
                        updatePageConfig({
                          table: { columnOrder: columnOrder as Array<{ id: AssetInspectionColumnId; active: boolean }> },
                        })
                      }
                    />
                  </div>
                </div>
              </>
            )}

            {selectedInspections.length > 0 && (
              <div className="flex items-center gap-1 px-2">
                <TablePageSelectedCountIndicator selectedCount={selectedInspections.length} totalCount={inspectionsData?.pages[0]?.totalCount ?? 0} />
                {canExportCsv && <TablePageHeaderActionButton icon={<DownloadCsvIcon />} label="Download CSV" onClick={handleCsvExport} />}
                <TablePageHeaderActionButton icon={<DownloadPdfIcon />} label="Download PDF" onClick={handlePdfExport} />
                {canBulkActOnSelectedInspections && (
                  <TablePageHeaderActionButton
                    icon={<DeleteIcon className="text-default-red" />}
                    label="Delete"
                    onClick={() => setOpenDeleteConfirmation(true)}
                  />
                )}
              </div>
            )}
          </div>
        </div>
      </TablePageHeader>

      <ScrollArea type="always" viewportRef={setScrollRoot} className="relative flex flex-1 flex-col">
        <ScrollBar orientation="horizontal" />
        <AssetInspectionsTable
          data={flattenedData}
          loading={(isFetchingInspections && !isFetchingNextInspectionsPage) || isLoadingPageConfig || isLoadingUnits}
          units={units ?? []}
          notFoundDisplay={
            debouncedSearch.length > 0 ? (
              <NoInspectionFoundFromSearchDisplay />
            ) : filters && filters.length > 0 ? (
              <NoInspectionFoundFromFiltersDisplay removeColumnFilters={() => setModuleFilters(MODULE.AssetInspections, [])} />
            ) : (
              <NoInspectionAvailableDisplay />
            )
          }
          rowSelection={selectedRows}
          onRowSelectionChange={setSelectedRows}
          columnFilters={filters || []}
          onColumnFiltersChange={(filters) => setModuleFilters(MODULE.AssetInspections, filters)}
          sorting={sorting}
          grouping={grouping}
          columnOrder={columnOrder}
          columnVisibility={columnVisibility}
          onSortingChange={(newSorting) =>
            updatePageConfig({
              table: { sorting: newSorting },
            })
          }
        />
        {hasNextInspectionsPage && (
          <Button ref={sentinelRef} onClick={loadMore} disabled={!canLoadMore} className="mx-auto w-full text-center" variant="ghost">
            {isFetchingNextInspectionsPage ? 'Loading more...' : 'Load Newer'}
          </Button>
        )}
      </ScrollArea>

      <DeleteConfirmationDialog
        open={openDeleteConfirmation}
        onOpenChange={setOpenDeleteConfirmation}
        callback={handleDeleteInspections}
        loading={isDeletingInspections}
        title="Delete inspections?"
        description="This action is permanent and cannot be undone. Are you sure you want to delete these inspections?"
      />
    </div>
  );
};

export default AssetInspectionsPage;
